import { useState, useCallback } from 'react';
import { usePermissions, PermissionAction } from './usePermissions';

const ACTION_LABELS: Record<PermissionAction, string> = {
  view: 'visualizar registros',
  create: 'criar novos registros',
  edit: 'editar registros',
  delete: 'excluir registros',
};

export function usePermissionCheck(module: string) {
  const { canAccess, isAdminOrOwner, loading, role } = usePermissions();
  const [showAlert, setShowAlert] = useState(false);
  const [alertAction, setAlertAction] = useState('');

  /**
   * Check if user has permission for an action.
   * If not, shows the permission alert and returns false.
   */
  const checkPermission = useCallback((action: PermissionAction): boolean => {
    if (loading) return false;
    
    if (canAccess(module, action)) {
      return true;
    }
    
    // Show permission alert
    setAlertAction(ACTION_LABELS[action] || action);
    setShowAlert(true);
    return false;
  }, [module, canAccess, loading]);

  /**
   * Check if user can view the module (without showing alert).
   * Used to conditionally render content.
   */
  const canView = useCallback((): boolean => {
    if (loading) return true; // Show content while loading
    return canAccess(module, 'view');
  }, [module, canAccess, loading]);

  return {
    checkPermission,
    canView,
    showAlert,
    setShowAlert,
    alertAction,
    loading,
    isAdminOrOwner,
    role,
  };
}
